import type { EditorState } from "prosemirror-state";
import { TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { EDITOR_MODE } from "./constants";
import { formatSourceMarkdownLink } from "./markdown/source-utils";
import { focusSchema } from "./schema";

type LinkValues = {
  href: string;
  text: string;
  title: string;
};

type RichLinkTarget = {
  from: number;
  to: number;
  type: "rich-link";
};

type RichSelectionTarget = {
  from: number;
  to: number;
  type: "rich-selection";
};

type SourceLinkTarget = {
  from: number;
  text: string;
  to: number;
  type: "source-link";
};

type SourceSelectionTarget = {
  from: number;
  to: number;
  type: "source-selection";
};

export type EditorLinkTarget =
  | RichLinkTarget
  | RichSelectionTarget
  | SourceLinkTarget
  | SourceSelectionTarget;

export type EditorLinkDialogState = LinkValues & {
  editing: boolean;
  target: EditorLinkTarget;
};

type RichLinkRange = RichLinkTarget & {
  href: string;
  title: string;
};

type SourceLinkRange = SourceLinkTarget & {
  href: string;
  title: string;
};

const getSelectionRange = (state: EditorState) => ({
  from: Math.min(state.selection.from, state.selection.to),
  to: Math.max(state.selection.from, state.selection.to),
});

const rangesOverlap = (
  from: number,
  to: number,
  targetFrom: number,
  targetTo: number,
) => from < targetTo && to > targetFrom;

const sameLinkMark = (
  left: { href?: unknown; title?: unknown },
  right: { href?: unknown; title?: unknown },
) => left.href === right.href && (left.title || "") === (right.title || "");

const findRichLinkRange = (state: EditorState): RichLinkRange | null => {
  const linkMarkType = focusSchema.marks.link;
  const { from: selectionFrom, to: selectionTo } = getSelectionRange(state);
  const empty = selectionFrom === selectionTo;
  const ranges: RichLinkRange[] = [];

  state.doc.descendants((node, pos) => {
    if (!node.isText) {
      return true;
    }

    const mark = node.marks.find(
      (candidate) => candidate.type === linkMarkType,
    );

    if (!mark) {
      return true;
    }

    const from = pos;
    const to = pos + node.nodeSize;
    const previous = ranges.at(-1);

    if (
      previous &&
      previous.to === from &&
      sameLinkMark(previous, mark.attrs)
    ) {
      previous.to = to;
      return true;
    }

    ranges.push({
      from,
      href: String(mark.attrs.href || ""),
      title: String(mark.attrs.title || ""),
      to,
      type: "rich-link",
    });

    return true;
  });

  return (
    ranges.find((range) =>
      empty
        ? range.from <= selectionFrom && selectionFrom <= range.to
        : rangesOverlap(range.from, range.to, selectionFrom, selectionTo),
    ) ?? null
  );
};

const sourceLinkPattern = /(!?)\[([^\]\n]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;

const findSourceLinkRange = (state: EditorState): SourceLinkRange | null => {
  const { from: selectionFrom, to: selectionTo } = getSelectionRange(state);
  const empty = selectionFrom === selectionTo;
  let result: SourceLinkRange | null = null;

  state.doc.descendants((node, pos) => {
    if (result || !node.isText || !node.text) {
      return !result;
    }

    for (const match of node.text.matchAll(sourceLinkPattern)) {
      if (match[1]) {
        continue;
      }

      const index = match.index ?? 0;
      const from = pos + index;
      const to = from + match[0].length;

      if (
        empty
          ? from <= selectionFrom && selectionFrom <= to
          : rangesOverlap(from, to, selectionFrom, selectionTo)
      ) {
        result = {
          from,
          href: match[3] ?? "",
          text: match[2] ?? "",
          title: match[4] ?? "",
          to,
          type: "source-link",
        };
        return false;
      }
    }

    return true;
  });

  return result;
};

export const getEditorLinkDialogState = (
  view: EditorView,
  mode: EDITOR_MODE,
): EditorLinkDialogState => {
  const { from, to } = getSelectionRange(view.state);

  if (mode === EDITOR_MODE.SIMPLE) {
    const sourceLink = findSourceLinkRange(view.state);

    if (sourceLink) {
      return {
        editing: true,
        href: sourceLink.href,
        target: sourceLink,
        text: sourceLink.text,
        title: sourceLink.title,
      };
    }

    return {
      editing: false,
      href: "",
      target: { from, to, type: "source-selection" },
      text: view.state.doc.textBetween(from, to),
      title: "",
    };
  }

  const richLink = findRichLinkRange(view.state);

  if (richLink) {
    return {
      editing: true,
      href: richLink.href,
      target: richLink,
      text: view.state.doc.textBetween(richLink.from, richLink.to),
      title: richLink.title,
    };
  }

  return {
    editing: false,
    href: "",
    target: { from, to, type: "rich-selection" },
    text: view.state.doc.textBetween(from, to),
    title: "",
  };
};

const getLinkText = ({ href, text }: Pick<LinkValues, "href" | "text">) =>
  text.trim() || href.trim();

export const applyEditorLink = (
  view: EditorView,
  target: EditorLinkTarget,
  values: LinkValues,
) => {
  const href = values.href.trim();
  const text = getLinkText(values);

  if (!href || !text) {
    return false;
  }

  if (target.type === "source-link" || target.type === "source-selection") {
    const markdown = formatSourceMarkdownLink({
      href,
      text,
      title: values.title.trim(),
    });
    const tr = view.state.tr.insertText(markdown, target.from, target.to);

    tr.setSelection(
      TextSelection.create(tr.doc, target.from + markdown.length),
    );
    view.dispatch(tr);
    view.focus();
    return true;
  }

  const linkMark = focusSchema.marks.link.create({
    href,
    title: values.title.trim() || null,
  });
  const linkText = focusSchema.text(text, [linkMark]);

  if (target.type === "rich-link") {
    const tr = view.state.tr.replaceWith(target.from, target.to, linkText);

    tr.setSelection(TextSelection.create(tr.doc, target.from + text.length));
    view.dispatch(tr);
    view.focus();
    return true;
  }

  const tr = view.state.tr.setSelection(
    TextSelection.create(view.state.doc, target.from, target.to),
  );

  tr.replaceSelectionWith(linkText, false);
  tr.setSelection(TextSelection.create(tr.doc, target.from + text.length));
  view.dispatch(tr.scrollIntoView());
  view.focus();
  return true;
};

export const removeEditorLink = (
  view: EditorView,
  target: EditorLinkTarget,
) => {
  if (target.type === "source-link") {
    const tr = view.state.tr.insertText(target.text, target.from, target.to);

    tr.setSelection(
      TextSelection.create(tr.doc, target.from + target.text.length),
    );
    view.dispatch(tr);
    view.focus();
    return true;
  }

  if (target.type === "rich-link") {
    view.dispatch(
      view.state.tr.removeMark(target.from, target.to, focusSchema.marks.link),
    );
    view.focus();
    return true;
  }

  return false;
};
